import { useMemo, useState } from 'react'
import { Sparkles, AlertTriangle, Search } from 'lucide-react'
import { usePilotLeads, type PilotLead } from '../../api/pilotLeads'

/**
 * Superadmin-only list of pilot-application submissions.
 *
 * Reads from ``GET /pilot-leads/admin``. The route in ``App.tsx`` is nested
 * under ``<SuperadminGate>`` so non-superadmins never render this page; the
 * backend ``require_superadmin`` is the authoritative gate.
 */
export function PilotLeadsPage() {
  const [search, setSearch] = useState('')
  const query = usePilotLeads({
    search: search.trim() || undefined,
    limit: 200,
  })

  const total = query.data?.total ?? 0

  // Backend already orders by created_at DESC, but we re-sort here as
  // belt-and-suspenders so the page stays consistent if a future change
  // shifts ordering server-side.
  const sortedLeads = useMemo(
    () =>
      [...(query.data?.pilot_leads ?? [])].sort(
        (a, b) => b.created_at - a.created_at,
      ),
    [query.data?.pilot_leads],
  )

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Sparkles className="h-6 w-6 text-primary" />
          Pilot leads
        </h1>
        <p className="text-sm text-text-secondary">
          Superadmin view of every submission to the public pilot application
          form on the home page. Email alerts are sent at submit-time; a
          warning badge appears when delivery failed.
        </p>
      </header>

      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <div className="font-medium">Submissions</div>
            <div className="text-xs text-text-secondary">
              {total} total
              {query.isFetching ? ' · refreshing…' : ''}
            </div>
          </div>
          <label className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-secondary" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, business, email, phone"
              className="w-72 rounded border border-border bg-background py-1.5 pl-7 pr-2 text-sm"
            />
          </label>
        </header>

        {query.isLoading ? (
          <div className="px-4 py-8 text-sm text-text-secondary">Loading…</div>
        ) : query.isError ? (
          <div className="px-4 py-8 text-sm text-error">
            Failed to load pilot leads: {String(query.error)}
          </div>
        ) : sortedLeads.length === 0 ? (
          <div className="px-4 py-8 text-sm text-text-secondary">
            No pilot leads yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse text-sm">
              <thead className="bg-surface-elevated text-left text-xs uppercase tracking-wide text-text-secondary">
                <tr>
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Business</th>
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-4 py-2 font-semibold">Phone</th>
                  <th className="px-4 py-2 font-semibold">Use case</th>
                  <th className="px-4 py-2 font-semibold">Notify</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedLeads.map((lead) => (
                  <PilotLeadRow key={lead.id} lead={lead} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function formatDateTime(epochSec: number): string {
  return new Date(epochSec * 1000)
    .toISOString()
    .replace('T', ' ')
    .slice(0, 16)
}

function PilotLeadRow({ lead }: { lead: PilotLead }) {
  const excerpt = lead.use_case
    ? lead.use_case.length > 160
      ? `${lead.use_case.slice(0, 160)}…`
      : lead.use_case
    : '—'
  return (
    <tr className="align-top">
      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-text-secondary">
        {formatDateTime(lead.created_at)}
      </td>
      <td className="px-4 py-3 font-medium">{lead.name || '—'}</td>
      <td className="px-4 py-3 text-text-secondary">{lead.business || '—'}</td>
      <td className="px-4 py-3">
        {lead.email ? (
          <a
            className="text-primary hover:underline"
            href={`mailto:${lead.email}`}
          >
            {lead.email}
          </a>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-3">
        {lead.phone ? (
          <a
            className="text-primary hover:underline"
            href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}
          >
            {lead.phone}
          </a>
        ) : (
          '—'
        )}
      </td>
      <td className="max-w-md px-4 py-3 text-text-secondary">
        <div className="whitespace-pre-wrap break-words">{excerpt}</div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        {lead.notify_error ? (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-error/30 bg-error/5 px-2 py-0.5 text-xs text-error"
            title={lead.notify_error}
          >
            <AlertTriangle className="h-3 w-3" />
            failed
          </span>
        ) : lead.notified_at ? (
          <span className="inline-flex items-center rounded-full border border-success/30 bg-success/5 px-2 py-0.5 text-xs text-success">
            sent
          </span>
        ) : (
          <span className="text-xs text-text-secondary">pending</span>
        )}
      </td>
    </tr>
  )
}
